import React, { FC, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FreeTextFilterComponent from 'components/users/shared/free-text-filter/free-text-filter.component';
import FilterOnStatusDropdown from 'components/users/shared/filter-on-status-dropdown/filter-on-status-dropdown.component';
import { OrganizationListFilter } from 'models/common';
import CustomerTypeFilter, { CustomerType } from 'components/users/shared/customer-type-filter/customer-type-filter';

type ClinicPageActionsProps = {
    onFilterChange: (filter: OrganizationListFilter) => void;
    onAddClinicDialogOpen: () => void;
    onAddLocalClinicDialogOpen: () => void;
};
const ClinicPageActions: FC<ClinicPageActionsProps> = ({
    onFilterChange,
    onAddClinicDialogOpen,
    onAddLocalClinicDialogOpen,
}: ClinicPageActionsProps) => {
    const { t: translate } = useTranslation(['organization']);
    const [filter, setFilter] = useState<OrganizationListFilter>({
        freeText: '',
        active: true,
        local: null,
        isWhiteLabeled: null,
    });

    const updateFilter = (partial: Partial<OrganizationListFilter>) => {
        const nextFilter: OrganizationListFilter = {
            ...filter,
            ...partial,
        };

        setFilter(nextFilter);
        onFilterChange(nextFilter);
    };

    const handleFreeTextChange = (freeText: string) => {
        updateFilter({ freeText });
    };

    const handleStatusSelected = (active: boolean | null) => {
        updateFilter({ active });
    };
    const apiFilterToCustomerType = (filter: OrganizationListFilter | null | undefined): CustomerType => {
        if (!filter) {
            return 'ALL';
        }

        if (filter.local == null && filter.isWhiteLabeled == null) {
            return 'ALL';
        }

        if (filter.local) {
            return 'LOCAL';
        }

        if (filter.isWhiteLabeled) {
            return 'DENTHUB';
        }

        return 'DENTHUB_PRO';
    };

    const mapCustomerTypeToFilter = (type: CustomerType) => {
        switch (type) {
            case 'ALL':
                return { local: null, isWhiteLabeled: null };
            case 'DENTHUB_PRO':
                return { local: false, isWhiteLabeled: false };
            case 'DENTHUB':
                return { local: false, isWhiteLabeled: true };
            case 'LOCAL':
                return { local: true, isWhiteLabeled: false };
        }
    };

    const handleCustomerTypeSelection = (customerType: CustomerType) => {
        const { local, isWhiteLabeled } = mapCustomerTypeToFilter(customerType);
        updateFilter({ local, isWhiteLabeled });
    };
    return (
        <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
            <Grid container spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <Grid item sm={3}>
                    <FreeTextFilterComponent
                        value={filter.freeText}
                        freeTextFilterChange={handleFreeTextChange}
                        label="search clinic freetext"
                    />
                </Grid>
                <Grid item>
                    <FilterOnStatusDropdown statusSelected={handleStatusSelected} />
                </Grid>
                <Grid item>
                    <CustomerTypeFilter
                        value={apiFilterToCustomerType(filter)}
                        onTypeSelect={handleCustomerTypeSelection}
                        showAllOption
                    />
                </Grid>
            </Grid>

            <Box display="flex" gap={2}>
                <Button variant="contained" onClick={onAddClinicDialogOpen}>
                    {translate('organization:ADD_CLINIC')}
                </Button>

                <Button variant="contained" onClick={onAddLocalClinicDialogOpen}>
                    {translate('organization:ADD_CLINIC_LOCAL')}
                </Button>
            </Box>
        </Box>
    );
};

export default ClinicPageActions;
